import { getDocumentIdToQueueItemMap } from '@/helpers/queues';
import { useReinstateContractMutation } from '@/services/contract';
import { Badge, Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useMemo, useState } from 'react';

import {
  DocumentFile,
  DocumentDocument,
  DocumentStatus,
  DocumentTypes,
  documentTitles,
  UserPermissions,
  UtilityBillRejectionReasons,
  QueueRejectionReason,
  QueueRejectionReasons,
  QueueStatus,
  QueueType,
} from 'types';

import { useQueueClaimConflict } from '../../hooks/useQueueClaimConflict';
import { useArchiveDocumentMutation, useUpdateDocumentStatusMutation } from '../../services/documents';
import { isErrorWithData } from '../../services/helpers';
import { useLazyGetQueuesByAccountIdQuery, useUpdateQueueMutation } from '../../services/queues';
import { documentStatus, documentRejectionReasons } from '../../types/Documents';
import { AllInstallPackageRejectionReasons } from '../../types/ReviewPackage';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import { ReinstateContractModal } from '../Contracts/ReinstateContractModal';
import { DocumentRejectionModal } from '../DocumentRejectionModal/DocumentRejectionModal';
import { RejectionModal } from '../Reject/RejectionModal';

import { FileItem } from './FileItem';

export function DocumentListItem({
  item,
  showDocumentActions = true,
  allowArchive = true,
  task,
  queue,
  rejections = [],
}: {
  item: DocumentDocument;
  showDocumentActions?: boolean;
  allowArchive?: boolean;
  task: any;
  queue?: any;
  rejections?: QueueRejectionReasons[];
}) {
  const { type, status, externalReference, files, name } = item;
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [updateDocumentStatus, { isLoading: isUpdatingStatus }] = useUpdateDocumentStatusMutation();
  const [archiveDocument, { isLoading: isArchiving }] = useArchiveDocumentMutation();
  const [reinstateContract, { isLoading: isReinstatingContract }] = useReinstateContractMutation();
  // A document's own name wins over the generic per-type label when the creator set one.
  const documentTitle = name || documentTitles[type] || (type as string);
  const rejectionReasons = documentRejectionReasons[type];
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const [fetchQueuesByAccountId, { isFetching: isFetchingQueue }] = useLazyGetQueuesByAccountIdQuery();
  const { user } = useAuth();
  const canArchive = allowArchive && status !== DocumentStatus.approved && status !== DocumentStatus.rejected;
  const allowsAction = useIsOrgReadOnly();

  const [freshQueue, setFreshQueue] = useState<any>(undefined);
  const effectiveQueue = freshQueue !== undefined ? freshQueue : queue;
  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(effectiveQueue);

  const resolveLatestQueue = async (): Promise<any> => {
    if (!queue && type !== DocumentTypes.utilityBill) return queue;

    const accountId = item.accountId?.toString();
    if (!accountId) return queue;

    try {
      const freshQueues = await fetchQueuesByAccountId({ accountId });
      const byDocumentId = getDocumentIdToQueueItemMap(freshQueues)[item.id?.toString() ?? ''];
      let resolved = byDocumentId ?? null;

      // Utility bill queues don't track individual document file IDs; fall back to type lookup.
      if (!resolved && type === DocumentTypes.utilityBill) {
        resolved = freshQueues?.find((q: any) => q.type === QueueType.utilityBill) ?? null;
      }

      if (resolved) {
        setFreshQueue(resolved);
      }

      return resolved ?? queue;
    } catch {
      return queue;
    }
  };

  const handleQueueUpdate = async (
    targetQueue: any,
    queueStatus: QueueStatus,
    note?: string,
    reasons?: QueueRejectionReason[],
  ) => {
    try {
      await updateQueue({
        id: targetQueue.id,
        data: {
          assigneeId: user?.sub,
          status: queueStatus,
          ...(reasons && {
            rejectionReasons: [
              {
                reasons,
                rejectedDocumentId: item?.id,
                ...(note && { note }),
              },
            ],
          }),
        },
      }).unwrap();
      toast.success('Queue updated successfully');
    } catch (e) {
      if (isErrorWithData(e)) {
        toast.error(`Error updating queue: ${e.data.message}`);
      } else {
        console.error(e);
        toast.error('Error updating queue');
      }
    }
  };

  const rejectionReasonOptions = useMemo(() => {
    if (!rejectionReasons) {
      return [];
    }
    return rejectionReasons.map((key) => ({
      label: AllInstallPackageRejectionReasons[key],
      value: key,
    }));
  }, [rejectionReasons]);

  const getReasonLabel = (reason: string): string => {
    return (
      (QueueRejectionReason as Record<string, string>)[reason] ||
      (UtilityBillRejectionReasons as Record<string, string>)[reason] ||
      reason
    );
  };

  const labelsPerRejection = useMemo(
    () => rejections.map((rej) => (rej.reasons ?? []).map(getReasonLabel).join(', ')),
    [rejections],
  );

  const handleDocumentStatusUpdate = async (
    status: DocumentStatus,
    rejectionNote?: string,
    rejectionReasons?: string[],
  ) => {
    try {
      await updateDocumentStatus({
        id: item?.id?.toString() || '',
        accountId: item?.accountId?.toString() || '',
        status,
        rejectionNote,
        rejectionReasons,
      }).unwrap();
      toast.success('Document updated successfully');
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating document: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating document');
      }
    }
  };

  const approve = async () => {
    const confirm = window.confirm('Are you sure you want to approve this document? This action cannot be undone.');
    if (!confirm) return;
    try {
      const resolved = await resolveLatestQueue();
      if (resolved) {
        return checkClaimAndProceed(() => handleQueueUpdate(resolved, QueueStatus.approved), resolved);
      }
      return await handleDocumentStatusUpdate(DocumentStatus.approved);
    } catch (e) {
      console.error(e);
      toast.error('Error approving document');
    }
  };

  const reject = async () => {
    const confirm = window.confirm('Are you sure you want to reject this document? This action cannot be undone.');
    if (!confirm) {
      return;
    }
    return handleDocumentStatusUpdate(DocumentStatus.rejected);
  };

  const archive = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to Archive this Document? This action cannot be undone.',
    );

    if (!confirmed) return;

    await archiveDocument({
      id: item?.id?.toString() || '',
      accountId: item?.accountId?.toString() || '',
      invalidateAllTags: true,
    });

    toast.success('Document archived successfully');
  };

  const handleRejectModalQueue = async (values: { rejectionReason: string; notes: string | undefined }) => {
    const { rejectionReason, notes } = values;
    await handleQueueUpdate(effectiveQueue, QueueStatus.rejected, notes, [rejectionReason as QueueRejectionReason]);
    closeModal();
  };

  const handleRejectDocumentWithEnum = async (values: { rejectionReason: string; notes: string | undefined }) => {
    await handleDocumentStatusUpdate(DocumentStatus.rejected, values.notes, [values.rejectionReason]);
    closeModal();
  };

  const handleRejectButtonClick = async () => {
    let resolvedQueue;
    try {
      resolvedQueue = await resolveLatestQueue();
    } catch (e) {
      console.error(e);
      toast.error('Error fetching latest queue');
      return;
    }
    checkClaimAndProceed(openModal, resolvedQueue);
  };

  const handleRejectModal = async (value: {
    rejectionReason: { value: string; label: string }[];
    rejectionNote: string;
  }) => {
    const rejectionReasons = value.rejectionReason.map((reason) => reason.value);
    await handleDocumentStatusUpdate(DocumentStatus.rejected, value.rejectionNote, rejectionReasons);
    closeModal();
  };

  const handleReinstateContract = async () => {
    const toastId = toast.loading('Reinstating contract');
    try {
      await reinstateContract({
        accountId: String(item.accountId),
        externalReference: String(item.externalReference),
      }).unwrap();
      toast.success('Contract reinstated successfully');
      closeModal();
    } catch (e: any) {
      console.error(e);
      toast.error(e?.data?.message || 'Error reinstating contract');
    } finally {
      toast.dismiss(toastId);
    }
  };

  return (
    <Box borderWidth="xs 0 0 0" borderColor="separator" background="primary">
      <Box padding={{ base: 'md 0', tablet: '0' }} direction="row" width="100" alignItems="center">
        {claimConflictModal}
        {queue || type === DocumentTypes.utilityBill ? (
          <DocumentRejectionModal
            isModalOpen={isModalOpen}
            title={documentTitle}
            handleClose={closeModal}
            handleSubmit={effectiveQueue ? handleRejectModalQueue : handleRejectDocumentWithEnum}
            rejectionReasonEnum={type === DocumentTypes.utilityBill ? UtilityBillRejectionReasons : undefined}
          />
        ) : type === DocumentTypes.contract && status === DocumentStatus.voided ? (
          <ReinstateContractModal
            isModalOpen={isModalOpen}
            handleClose={closeModal}
            handleSubmit={handleReinstateContract}
          />
        ) : (
          <RejectionModal
            title={`Reject ${documentTitle}`}
            rejectionReasons={rejectionReasonOptions}
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            onSubmit={handleRejectModal}
          />
        )}
        <Box width={{ base: '100', tablet: '60' }} direction={{ base: 'column', tablet: 'row' }}>
          <Box
            alignItems="flex-start"
            width={{ base: '100', tablet: '30' }}
            padding={{ base: 'md lg', tablet: 'md md md lg' }}
          >
            <Badge message={documentStatus[status]?.label} variant={documentStatus[status]?.variant} />
          </Box>

          <Box
            width={{ base: '100', tablet: '70' }}
            display="block"
            childGap="xs"
            padding={{ base: '0 lg', tablet: 'md md md lg' }}
          >
            <Box fontWeight="medium">{documentTitle}</Box>
            <Box display="block">
              {files.map((f: DocumentFile) => (
                <FileItem file={f} key={f?.md5Hash} />
              ))}
            </Box>
            {externalReference && <Box fontSize="xs">{`Ext. Reference: ${externalReference}`}</Box>}

            {status === DocumentStatus.rejected &&
              (rejections.length > 0 ? (
                <Box color="danger" margin="xs 0 0 0">
                  {rejections.map((rej, idx) => (
                    <Box key={idx}>
                      {rej.note ? `${labelsPerRejection[idx]}: ${rej.note}` : labelsPerRejection[idx]}
                    </Box>
                  ))}
                </Box>
              ) : (
                task?.rejectionReasons && (
                  <Box color="danger" margin="xs 0 0 0">
                    {task?.rejectionReasons?.map((rej: any, idx: number) => {
                      return (
                        <Box key={idx}>
                          {rej.note
                            ? `${rej.reasons.map(getReasonLabel).join(', ')}: ${rej.note}`
                            : rej.reasons.join(', ')}
                        </Box>
                      );
                    })}
                  </Box>
                )
              ))}
          </Box>
        </Box>

        {showDocumentActions && (
          <Box
            width="40"
            padding={{ base: '0 lg', tablet: 'md lg' }}
            direction={{ base: 'column', tablet: 'row' }}
            childGap="xs"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            {status === DocumentStatus.pending && (
              <div>
                <RequirePalmettoFinanceUser>
                  <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                    <Button
                      size="sm"
                      variant="primary"
                      isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving || isFetchingQueue}
                      onClick={approve}
                    >
                      Approve
                    </Button>
                  </RequirePermissions>
                </RequirePalmettoFinanceUser>
              </div>
            )}
            {type !== DocumentTypes.contract && status === DocumentStatus.pending && (
              <div>
                <RequirePalmettoFinanceUser>
                  <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                    <Button
                      size="sm"
                      variant="primary"
                      tone="danger"
                      isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving || isFetchingQueue}
                      onClick={
                        queue || type === DocumentTypes.utilityBill
                          ? handleRejectButtonClick
                          : rejectionReasons?.length
                            ? openModal
                            : reject
                      }
                    >
                      Reject
                    </Button>
                  </RequirePermissions>
                </RequirePalmettoFinanceUser>
              </div>
            )}
            {type === DocumentTypes.contract && status === DocumentStatus.pending && (
              <div>
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    size="sm"
                    variant="primary"
                    tone="danger"
                    isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving}
                    onClick={reject}
                    disabled={!allowsAction}
                  >
                    Void
                  </Button>
                </RequirePermissions>
              </div>
            )}
            {type === DocumentTypes.contract && status === DocumentStatus.voided && (
              <div>
                <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                  <Button
                    size="sm"
                    variant="secondary"
                    tone="neutral"
                    isLoading={isReinstatingContract || isUpdatingStatus || isUpdatingQueue || isArchiving}
                    onClick={openModal}
                    disabled={!allowsAction}
                  >
                    Reinstate
                  </Button>
                </RequirePermissions>
              </div>
            )}
            {canArchive && (
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  isLoading={isReinstatingContract || isUpdatingStatus || isUpdatingQueue || isArchiving}
                  onClick={archive}
                  disabled={!allowsAction}
                >
                  Archive
                </Button>
              </RequirePermissions>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
